import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { products, categories, siteImages, BRAND_INFO } from '../data/mock';
import usePageTitle from '../hooks/usePageTitle';
import {
  ArrowUpRight, ArrowRight, ArrowLeft, ChevronDown, Scissors,
  Sparkles, Heart, Truck, ShieldCheck, MessageCircle
} from 'lucide-react';

const HomePage = () => {
  usePageTitle(
    'Stitch Your Dash | Sewing Tools & Creative DIY Supplies',
    'Shop premium sewing gadgets and DIY craft tools at Stitch Your Dash. Quality rotary cutters, sewing machines, fabric tools and more.'
  );

  const carouselRef = useRef(null);

  const scrollCarousel = (direction) => {
    if (!carouselRef.current) return;
    const card = carouselRef.current.querySelector('a');
    const cardWidth = card ? card.offsetWidth + 24 : 360;
    carouselRef.current.scrollBy({ left: direction * cardWidth, behavior: 'smooth' });
  };

  const featuredProducts = products.slice(0, 8);
  const today = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // Numbered values for the index/list section (NOT a card grid)
  const principles = [
    { num: '01', t: 'Easy to Use', d: 'Intuitive design that puts the focus on your craft, not the learning curve.' },
    { num: '02', t: 'Made for DIY', d: 'Every tool is selected with home crafters and weekend makers in mind.' },
    { num: '03', t: 'Time-Saving', d: 'Smart accessories that take the friction out of every project.' },
    { num: '04', t: 'Beginner-Friendly', d: 'Confidence-boosting tools that grow with your skills.' },
  ];

  const reasons = [
    { num: '01', icon: Heart, t: 'Quality Tools', d: 'Each product is curated for cozy, premium-grade craftsmanship.' },
    { num: '02', icon: Truck, t: 'Fast USA Shipping', d: 'Carefully packed and on its way to your studio in days, not weeks.' },
    { num: '03', icon: ShieldCheck, t: 'Secure Checkout', d: '256-bit SSL encryption keeps every order safe from start to finish.' },
    { num: '04', icon: MessageCircle, t: 'Real Support', d: 'Our team replies fast, with answers from real makers — not bots.' },
  ];

  return (
    <div className="min-h-screen bg-warmWhite-100">
      <Header />
      <CartDrawer />

      <main>
        {/* ============== 1. COVER HERO - Full-viewport, centered, dominant typography over single image ============== */}
        <section className="relative h-[100svh] min-h-[640px] w-full overflow-hidden">
          {/* Full-bleed image */}
          <img
            src={siteImages.heroMain}
            alt="Cozy sewing workspace"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-warmBrown-950/65 via-warmBrown-950/35 to-warmBrown-950/85" />
          <div className="absolute inset-0 pattern-dots opacity-25" />

          {/* Top magazine masthead */}
          <div className="absolute top-[64px] md:top-[105px] lg:top-[125px] inset-x-0 z-10">
            <div className="container-wide pt-6 lg:pt-10">
              <div className="flex items-center justify-between text-cream-50 text-[11px] tracking-[0.22em] uppercase">
                <span className="flex items-center gap-2">
                  <span className="w-8 h-px bg-cream-50" />
                  Issue 01 — The Studio Edition
                </span>
                <span className="hidden sm:inline opacity-70">{today}</span>
              </div>
            </div>
          </div>

          {/* Centered hero typography */}
          <div className="relative h-full flex flex-col items-center justify-center px-6 text-center text-cream-50">
            <span className="font-hand text-3xl sm:text-4xl text-rose-300 mb-2 sm:mb-4">cozy makers, welcome</span>
            <h1 className="font-display text-editorial-2xl max-w-[14ch] mx-auto">
              Sewn with <em className="italic-display text-rose-200 not-italic" style={{fontStyle:'italic'}}>love</em>, made for makers.
            </h1>
            <p className="mt-7 max-w-xl text-base sm:text-lg text-cream-100/85 leading-relaxed">
              {BRAND_INFO.tagline}. A small, hand-picked catalog of tools that turn quiet afternoons into beautiful, handmade memories.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/shop" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-rose-500 hover:bg-rose-600 text-cream-50 font-medium rounded-full shadow-glow-rose transition-colors">
                Shop the Collection
                <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/about" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent border border-cream-50/50 hover:bg-cream-50 hover:text-warmBrown-950 text-cream-50 font-medium rounded-full transition-colors">
                Read the Story
              </Link>
            </div>
          </div>

          {/* Bottom scroll indicator */}
          <div className="absolute bottom-6 inset-x-0 flex items-center justify-center text-cream-100/80 text-[10px] uppercase tracking-[0.3em]">
            <span className="flex items-center gap-2 animate-pulse">
              <ChevronDown className="w-3 h-3" /> Scroll to explore <ChevronDown className="w-3 h-3" />
            </span>
          </div>
        </section>

        {/* ============== 2. BENTO EDITORIAL GRID - 12-col asymmetric mosaic ============== */}
        <section className="py-20 lg:py-32 bg-warmWhite-100">
          <div className="container-wide">
            {/* Section header (table-of-contents style) */}
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10 lg:mb-14">
              <div>
                <span className="font-hand text-2xl text-rose-500">page — 002</span>
                <h2 className="font-display text-editorial-lg text-warmBrown-950 mt-1">The Studio.</h2>
              </div>
              <p className="max-w-md text-warmBrown-600 leading-relaxed">
                A peek inside our cozy little corner of cottons, threads and clever tools.
              </p>
            </div>

            <div className="grid grid-cols-12 gap-3 lg:gap-5 auto-rows-[minmax(140px,auto)]">
              {/* Big lifestyle tile - spans 7 cols, 2 rows */}
              <div className="col-span-12 sm:col-span-7 row-span-2 relative overflow-hidden rounded-[12px] min-h-[360px] lg:min-h-[480px]">
                <img src={siteImages.heroSecondary} alt="Threads close-up" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-warmBrown-950/70 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-cream-50">
                  <span className="font-hand text-2xl text-rose-300">our atelier</span>
                  <h3 className="font-display text-3xl lg:text-5xl mt-1">Threads,<br /><em className="italic-display not-italic" style={{fontStyle:'italic'}}>by the spoolful.</em></h3>
                </div>
              </div>

              {/* Quote tile */}
              <div className="col-span-6 sm:col-span-5 relative overflow-hidden rounded-[12px] bg-warmBrown-950 text-cream-50 p-7 lg:p-10">
                <Sparkles className="w-8 h-8 text-rose-400" />
                <p className="mt-4 font-display text-xl lg:text-2xl leading-snug">
                  Slow craft. <em className="italic-display text-rose-300 not-italic" style={{fontStyle:'italic'}}>Steady joy.</em>
                </p>
                <p className="mt-4 text-cream-100/70 text-sm">
                  Curated tools that respect your time — and your pace.
                </p>
              </div>

              {/* Featured product tile */}
              <Link to={`/product/${products[3].slug}`} className="col-span-6 sm:col-span-5 relative overflow-hidden rounded-[12px] bg-rose-500 text-cream-50 p-7 lg:p-9 group">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-rose-100">
                  <span className="w-6 h-px bg-rose-100" /> Maker’s pick
                </div>
                <h3 className="font-display text-2xl lg:text-3xl mt-3 leading-tight">{products[3].name}</h3>
                <p className="mt-3 font-display text-4xl lg:text-5xl">${products[3].price.toFixed(2)}</p>
                <div className="mt-5 inline-flex items-center gap-2 text-sm text-rose-100 group-hover:gap-3 transition-all">
                  Shop this <ArrowUpRight className="w-4 h-4" />
                </div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 lg:w-44 lg:h-44 rounded-2xl overflow-hidden bg-rose-50 rotate-6">
                  <img src={products[3].image} alt={products[3].name} className="w-full h-full object-contain" />
                </div>
              </Link>

              {/* Three small category tiles row */}
              {categories.slice(0, 3).map((cat, idx) => (
                <Link
                  key={cat.id}
                  to="/shop"
                  className={`relative overflow-hidden rounded-[12px] bg-cream-100 group min-h-[180px] ${idx === 0 ? 'col-span-12 sm:col-span-4' : idx === 1 ? 'col-span-6 sm:col-span-4' : 'col-span-6 sm:col-span-4'}`}
                >
                  <img src={cat.image} alt={cat.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 mix-blend-multiply opacity-90" />
                  <div className="absolute inset-0 bg-gradient-to-br from-cream-100/30 via-cream-100/10 to-cream-100/40" />
                  <div className="relative h-full p-6 flex flex-col justify-end">
                    <span className="font-hand text-xl text-rose-600">no. 0{idx + 1}</span>
                    <h3 className="font-display text-xl lg:text-2xl text-warmBrown-950 mt-1">{cat.name}</h3>
                    <span className="mt-2 text-[10px] uppercase tracking-[0.2em] text-warmBrown-600 inline-flex items-center gap-1">
                      Browse <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ============== 3. CHAPTER 01 - The Collection (horizontal scroll, NOT grid) ============== */}
        <section className="relative py-20 lg:py-32 bg-cream-50 overflow-hidden">
          <div className="container-wide">
            {/* Magazine 2-col intro */}
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 mb-12 lg:mb-16">
              <div className="lg:col-span-5">
                <span className="font-hand text-3xl text-rose-500">chapter — 01</span>
                <h2 className="font-display text-editorial-lg text-warmBrown-950 mt-1 leading-[0.95]">
                  Tools that <em className="italic-display text-rose-600 not-italic" style={{fontStyle:'italic'}}>last</em>.
                </h2>
              </div>
              <div className="lg:col-span-5 lg:col-start-7 lg:pt-6 columns-1 sm:columns-2 lg:columns-1 gap-6">
                <p className="text-warmBrown-700 leading-relaxed first-letter:font-display first-letter:text-6xl first-letter:float-left first-letter:mr-2 first-letter:mt-1 first-letter:text-rose-500 first-letter:leading-[0.85]">
                  Each piece in our small collection is hand-selected for quality, longevity, and the cozy joy it brings to your studio.
                </p>
                <p className="text-warmBrown-600 leading-relaxed mt-4 text-sm">
                  We don’t do massive catalogs. We do the right tools — the ones you’ll reach for again and again, season after season.
                </p>
              </div>
            </div>
          </div>

          {/* Horizontal scroll carousel - very different from a grid */}
          <div className="relative">
            <div
              ref={carouselRef}
              className="flex gap-5 lg:gap-7 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-6 pl-5 sm:pl-8 lg:pl-12 pr-5 sm:pr-8 lg:pr-12 [&::-webkit-scrollbar]:hidden"
              style={{ scrollbarWidth: 'none' }}
            >
              {featuredProducts.map((p, idx) => (
                <div key={p.id} className="flex-shrink-0 w-[78%] sm:w-[44%] lg:w-[28%] xl:w-[22%] snap-start">
                  <ProductCard product={p} index={idx} />
                </div>
              ))}
              <div className="flex-shrink-0 w-[20%] flex items-center pl-4">
                <Link to="/shop" className="font-display text-xl text-warmBrown-700 hover:text-rose-600 transition-colors flex items-center gap-2">
                  See all<br />{products.length} pieces <ArrowUpRight className="w-5 h-5" />
                </Link>
              </div>
            </div>

            {/* Carousel controls */}
            <div className="container-wide mt-2 flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-[0.22em] text-warmBrown-500">← swipe to explore</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => scrollCarousel(-1)}
                  className="w-12 h-12 rounded-full bg-warmWhite-100 hover:bg-warmBrown-900 hover:text-cream-50 text-warmBrown-900 border border-cream-300 flex items-center justify-center transition-colors"
                  aria-label="Previous"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => scrollCarousel(1)}
                  className="w-12 h-12 rounded-full bg-warmBrown-900 hover:bg-rose-600 text-cream-50 flex items-center justify-center transition-colors"
                  aria-label="Next"
                >
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ============== 4. CHAPTER 02 - Numbered list, NOT a card grid ============== */}
        <section className="py-20 lg:py-32 bg-warmWhite-100">
          <div className="container-wide">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
              {/* Left: chapter title sticky-feel */}
              <div className="lg:col-span-4 lg:sticky lg:top-32 lg:self-start">
                <span className="font-hand text-3xl text-rose-500">chapter — 02</span>
                <h2 className="font-display text-editorial-lg text-warmBrown-950 mt-1 leading-[0.95]">
                  For <em className="italic-display text-rose-600 not-italic" style={{fontStyle:'italic'}}>every</em> maker.
                </h2>
                <p className="mt-6 text-warmBrown-600 leading-relaxed">
                  Whether you’re hemming a curtain or stitching your fiftieth quilt, these are the principles guiding every product on our shelf.
                </p>
                <Link to="/why-choose-us" className="mt-8 inline-flex items-center gap-2 text-warmBrown-900 hover:text-rose-600 font-medium">
                  Why we exist <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {/* Right: stacked numbered list, NOT cards */}
              <div className="lg:col-span-7 lg:col-start-6 divide-y divide-warmBrown-900/10">
                {principles.map((p, idx) => (
                  <div key={idx} className="py-8 lg:py-12 grid grid-cols-12 gap-4 lg:gap-6 items-start group">
                    <span className="col-span-3 sm:col-span-2 font-display text-numero text-cream-300 group-hover:text-rose-400 transition-colors leading-none">
                      {p.num}
                    </span>
                    <div className="col-span-9 sm:col-span-10 lg:pl-6">
                      <h3 className="font-display text-2xl lg:text-3xl text-warmBrown-950">{p.t}</h3>
                      <p className="mt-2 text-warmBrown-600 leading-relaxed max-w-md">{p.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ============== 5. PULL QUOTE - Editorial spread ============== */}
        <section className="relative bg-warmBrown-950 text-cream-50 py-24 lg:py-40 overflow-hidden">
          <div className="absolute inset-0 opacity-30">
            <img src={siteImages.ctaBackground} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-warmBrown-950/60" />
          </div>
          <div className="absolute inset-0 pattern-dots opacity-15" />

          <div className="relative container-page">
            <div className="max-w-4xl">
              <Scissors className="w-10 h-10 text-rose-400 -rotate-12 mb-8" />
              <p className="font-display text-editorial-lg leading-[1.05]">
                <em className="italic-display text-rose-200 not-italic" style={{fontStyle:'italic'}}>“</em>
                Every stitch tells a story — we just make sure you have the right tools to write it.
                <em className="italic-display text-rose-200 not-italic" style={{fontStyle:'italic'}}>”</em>
              </p>
              <div className="mt-10 flex items-center gap-4">
                <span className="w-12 h-px bg-rose-300" />
                <span className="text-sm uppercase tracking-[0.2em] text-cream-200">
                  {BRAND_INFO.name} — Our Story
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* ============== 6. CHAPTER 03 - Bento inspiration with overlapping tiles ============== */}
        <section className="py-20 lg:py-32 bg-cream-50 relative overflow-hidden">
          <div className="container-wide">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10 lg:mb-14">
              <div>
                <span className="font-hand text-3xl text-rose-500">chapter — 03</span>
                <h2 className="font-display text-editorial-lg text-warmBrown-950 mt-1 leading-[0.95]">
                  Inspiration, <em className="italic-display text-rose-600 not-italic" style={{fontStyle:'italic'}}>found</em>.
                </h2>
              </div>
              <span className="font-hand text-2xl text-warmBrown-500">browse the lookbook</span>
            </div>

            <div className="grid grid-cols-12 gap-4 lg:gap-6">
              <div className="col-span-12 lg:col-span-5 relative aspect-[4/5] rounded-[12px] overflow-hidden">
                <img src={siteImages.inspirationA} alt="Crafting moment" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-warmBrown-950/60 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 text-cream-50">
                  <span className="font-hand text-2xl text-rose-300">spread 01</span>
                  <h3 className="font-display text-2xl lg:text-3xl mt-1">Quiet mornings.</h3>
                </div>
              </div>

              <div className="col-span-12 lg:col-span-7 grid grid-cols-2 grid-rows-2 gap-4 lg:gap-6">
                <div className="col-span-2 relative aspect-[5/2.5] rounded-[12px] overflow-hidden">
                  <img src={siteImages.inspirationBg} alt="Workshop" className="w-full h-full object-cover" />
                </div>
                <div className="relative aspect-square rounded-[12px] overflow-hidden">
                  <img src={siteImages.inspirationB} alt="Fabric" className="w-full h-full object-cover" />
                </div>
                <div className="relative aspect-square rounded-[12px] overflow-hidden bg-rose-500 p-6 flex flex-col justify-between text-cream-50">
                  <Heart className="w-7 h-7" />
                  <div>
                    <span className="font-hand text-xl text-rose-100">a small note</span>
                    <p className="font-display text-xl lg:text-2xl leading-snug mt-1">Made by hand. Loved by heart.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============== 7. INDEX - Why Choose Us as a Table of Contents (NOT cards) ============== */}
        <section className="py-20 lg:py-32 bg-warmWhite-100">
          <div className="container-wide">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12 lg:mb-16">
              <div>
                <span className="font-hand text-3xl text-rose-500">the index</span>
                <h2 className="font-display text-editorial-lg text-warmBrown-950 mt-1 leading-[0.95]">
                  Promises that come <em className="italic-display text-rose-600 not-italic" style={{fontStyle:'italic'}}>standard</em>.
                </h2>
              </div>
              <span className="text-sm uppercase tracking-[0.22em] text-warmBrown-500 hidden sm:block">— 0{reasons.length} entries</span>
            </div>

            <div className="border-t border-warmBrown-900/10">
              {reasons.map((r, idx) => (
                <div
                  key={idx}
                  className="border-b border-warmBrown-900/10 group hover:bg-cream-50 transition-colors"
                >
                  <div className="grid grid-cols-12 gap-4 lg:gap-6 py-7 lg:py-9 items-center">
                    <span className="col-span-2 lg:col-span-1 font-display text-3xl lg:text-5xl text-warmBrown-300 group-hover:text-rose-500 transition-colors">{r.num}</span>
                    <span className="col-span-2 lg:col-span-1 hidden lg:flex w-12 h-12 rounded-full bg-cream-100 group-hover:bg-rose-500 text-warmBrown-900 group-hover:text-cream-50 items-center justify-center transition-colors">
                      <r.icon className="w-5 h-5" />
                    </span>
                    <h3 className="col-span-10 lg:col-span-4 font-display text-xl lg:text-3xl text-warmBrown-950">{r.t}</h3>
                    <p className="col-span-12 lg:col-span-5 text-warmBrown-600 leading-relaxed">{r.d}</p>
                    <ArrowUpRight className="col-span-12 lg:col-span-1 w-5 h-5 text-warmBrown-400 group-hover:text-rose-500 group-hover:rotate-45 transition-all hidden lg:block ml-auto" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============== 8. COVER SPREAD CTA - Full bleed editorial ============== */}
        <section className="relative h-[80vh] min-h-[520px] overflow-hidden">
          <img src={siteImages.featuredBackground} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-warmBrown-950/85 via-warmBrown-950/70 to-rose-900/55" />

          <div className="relative h-full container-wide flex flex-col">
            {/* Top labels */}
            <div className="pt-12 lg:pt-20 flex justify-between text-cream-50/85 text-[11px] uppercase tracking-[0.22em]">
              <span>back cover — final spread</span>
              <span className="hidden sm:block">{BRAND_INFO.domain}</span>
            </div>

            {/* Centered headline */}
            <div className="flex-1 flex flex-col items-center justify-center text-center text-cream-50 px-4">
              <span className="font-hand text-4xl text-rose-300 mb-4">until next time</span>
              <h2 className="font-display text-editorial-2xl max-w-[14ch]">
                Create something <em className="italic-display text-rose-200 not-italic" style={{fontStyle:'italic'}}>beautiful</em> today.
              </h2>
              <Link
                to="/shop"
                className="mt-10 inline-flex items-center gap-3 px-9 py-4 bg-cream-50 hover:bg-rose-500 hover:text-cream-50 text-warmBrown-950 font-medium rounded-full transition-colors"
              >
                Begin your project
                <ArrowUpRight className="w-5 h-5" />
              </Link>
            </div>

            {/* Bottom labels */}
            <div className="pb-12 lg:pb-16 flex flex-col sm:flex-row sm:items-end justify-between gap-2 text-cream-50/85">
              <span className="font-display text-xl">— {BRAND_INFO.name}</span>
              <span className="text-[11px] uppercase tracking-[0.22em]">issue 01 · thank you for reading</span>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
